import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { mfaAPI, MFASetupResponse } from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';
import './MFASetup.css';

interface MFASetupProps {
  onComplete: () => void;
  onCancel: () => void;
}

type SetupStep = 'password' | 'qrcode' | 'verify' | 'recovery' | 'complete';

const MFASetup: React.FC<MFASetupProps> = ({ onComplete, onCancel }) => {
  const { t } = useTranslation(['auth', 'common']);
  const { branding } = useSettings();
  const [step, setStep] = useState<SetupStep>('password');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [setupData, setSetupData] = useState<MFASetupResponse | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showManualKey, setShowManualKey] = useState(false);
  const [codesCopied, setCodesCopied] = useState(false);

  const handleInitiateSetup = async () => {
    if (!password) {
      setError(t('auth:mfa.errors.passwordRequired', 'Password is required'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await mfaAPI.initiateSetup(password);
      setSetupData(response.data);
      setStep('qrcode');
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth:mfa.errors.setupFailed', 'Failed to initiate MFA setup'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!totpCode || totpCode.length !== 6) {
      setError(t('auth:mfa.errors.invalidCode', 'Please enter a 6-digit code'));
      return;
    }

    if (!setupData) {
      setError(t('auth:mfa.errors.noSetupData', 'Setup data not found'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await mfaAPI.verifySetup(setupData.setup_token, totpCode);
      setRecoveryCodes(response.data.recovery_codes);
      setStep('recovery');
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth:mfa.errors.verifyFailed', 'Invalid verification code'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyRecoveryCodes = async () => {
    const codesText = recoveryCodes.join('\n');
    try {
      await navigator.clipboard.writeText(codesText);
      setCodesCopied(true);
      setTimeout(() => setCodesCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy codes:', err);
    }
  };

  const handleDownloadRecoveryCodes = () => {
    const codesText = `${branding.applicationName} MFA Recovery Codes\n${'='.repeat(40)}\n\nKeep these codes in a safe place. Each code can only be used once.\n\n${recoveryCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}\n\nGenerated: ${new Date().toISOString()}`;

    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vulnscanner-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleComplete = () => {
    setStep('complete');
    setTimeout(() => {
      onComplete();
    }, 2000);
  };

  const renderPasswordStep = () => (
    <div className="mfa-step">
      <div className="mfa-step-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      </div>
      <h3>{t('auth:mfa.setup.confirmPassword', 'Confirm Your Password')}</h3>
      <p className="mfa-step-description">
        {t('auth:mfa.setup.confirmPasswordDesc', 'Enter your current password to begin setting up two-factor authentication.')}
      </p>

      <div className="mfa-form-group">
        <label>{t('auth:mfa.setup.password', 'Password')}</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t('auth:mfa.setup.enterPassword', 'Enter your password')}
          disabled={loading}
          autoFocus
        />
      </div>

      {error && <div className="mfa-error">{error}</div>}

      <div className="mfa-actions">
        <button className="mfa-btn mfa-btn-secondary" onClick={onCancel} disabled={loading}>
          {t('common:cancel', 'Cancel')}
        </button>
        <button className="mfa-btn mfa-btn-primary" onClick={handleInitiateSetup} disabled={loading}>
          {loading ? t('common:loading', 'Loading...') : t('common:continue', 'Continue')}
        </button>
      </div>
    </div>
  );

  const renderQRCodeStep = () => (
    <div className="mfa-step">
      <div className="mfa-step-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7"/>
          <rect x="14" y="3" width="7" height="7"/>
          <rect x="14" y="14" width="7" height="7"/>
          <rect x="3" y="14" width="7" height="7"/>
        </svg>
      </div>
      <h3>{t('auth:mfa.setup.scanQR', 'Scan QR Code')}</h3>
      <p className="mfa-step-description">
        {t('auth:mfa.setup.scanQRDesc', 'Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)')}
      </p>

      {setupData && (
        <div className="mfa-qr-container">
          <img
            src={setupData.qr_code_data_url}
            alt="MFA QR Code"
            className="mfa-qr-code"
          />

          <button
            className="mfa-manual-key-toggle"
            onClick={() => setShowManualKey(!showManualKey)}
          >
            {showManualKey
              ? t('auth:mfa.setup.hideManualKey', 'Hide manual entry key')
              : t('auth:mfa.setup.showManualKey', "Can't scan? Enter key manually")}
          </button>

          {showManualKey && (
            <div className="mfa-manual-key">
              <label>{t('auth:mfa.setup.manualKey', 'Manual Entry Key')}</label>
              <code className="mfa-secret-key">{setupData.manual_entry_key}</code>
              <small>{t('auth:mfa.setup.accountName', 'Account')}: {setupData.account_name}</small>
            </div>
          )}
        </div>
      )}

      <div className="mfa-actions">
        <button className="mfa-btn mfa-btn-secondary" onClick={() => setStep('password')}>
          {t('common:back', 'Back')}
        </button>
        <button className="mfa-btn mfa-btn-primary" onClick={() => setStep('verify')}>
          {t('common:continue', 'Continue')}
        </button>
      </div>
    </div>
  );

  const renderVerifyStep = () => (
    <div className="mfa-step">
      <div className="mfa-step-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
      </div>
      <h3>{t('auth:mfa.setup.verifyCode', 'Verify Setup')}</h3>
      <p className="mfa-step-description">
        {t('auth:mfa.setup.verifyCodeDesc', 'Enter the 6-digit code from your authenticator app to verify the setup.')}
      </p>

      <div className="mfa-form-group">
        <label>{t('auth:mfa.setup.verificationCode', 'Verification Code')}</label>
        <input
          type="text"
          value={totpCode}
          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          className="mfa-code-input"
          maxLength={6}
          disabled={loading}
          autoFocus
        />
      </div>

      {error && <div className="mfa-error">{error}</div>}

      <div className="mfa-actions">
        <button className="mfa-btn mfa-btn-secondary" onClick={() => setStep('qrcode')} disabled={loading}>
          {t('common:back', 'Back')}
        </button>
        <button className="mfa-btn mfa-btn-primary" onClick={handleVerifySetup} disabled={loading || totpCode.length !== 6}>
          {loading ? t('common:verifying', 'Verifying...') : t('common:verify', 'Verify')}
        </button>
      </div>
    </div>
  );

  const renderRecoveryStep = () => (
    <div className="mfa-step">
      <div className="mfa-step-icon mfa-step-icon-warning">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
      <h3>{t('auth:mfa.setup.saveRecoveryCodes', 'Save Your Recovery Codes')}</h3>
      <p className="mfa-step-description mfa-warning-text">
        {t('auth:mfa.setup.recoveryCodesWarning', 'Save these recovery codes in a secure location. Each code can only be used once. If you lose access to your authenticator, you can use these codes to sign in.')}
      </p>

      <div className="mfa-recovery-codes">
        {recoveryCodes.map((code, index) => (
          <div key={index} className="mfa-recovery-code">
            <span className="mfa-recovery-code-num">{index + 1}.</span>
            <code>{code}</code>
          </div>
        ))}
      </div>

      <div className="mfa-recovery-actions">
        <button
          className="mfa-btn mfa-btn-secondary"
          onClick={handleCopyRecoveryCodes}
        >
          {codesCopied
            ? t('auth:mfa.setup.copied', 'Copied!')
            : t('auth:mfa.setup.copyAll', 'Copy All')}
        </button>
        <button
          className="mfa-btn mfa-btn-secondary"
          onClick={handleDownloadRecoveryCodes}
        >
          {t('auth:mfa.setup.download', 'Download')}
        </button>
      </div>

      <div className="mfa-confirmation">
        <label className="mfa-checkbox-label">
          <input
            type="checkbox"
            checked={codesCopied}
            onChange={(e) => setCodesCopied(e.target.checked)}
          />
          {t('auth:mfa.setup.confirmSaved', 'I have saved my recovery codes')}
        </label>
      </div>

      <div className="mfa-actions">
        <button
          className="mfa-btn mfa-btn-primary"
          onClick={handleComplete}
          disabled={!codesCopied}
        >
          {t('auth:mfa.setup.complete', 'Complete Setup')}
        </button>
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="mfa-step mfa-step-complete">
      <div className="mfa-step-icon mfa-step-icon-success">
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
      </div>
      <h3>{t('auth:mfa.setup.setupComplete', 'Setup Complete!')}</h3>
      <p className="mfa-step-description">
        {t('auth:mfa.setup.setupCompleteDesc', 'Two-factor authentication is now enabled for your account.')}
      </p>
    </div>
  );

  return (
    <div className="mfa-setup-overlay">
      <div className="mfa-setup-modal">
        <div className="mfa-setup-header">
          <h2>{t('auth:mfa.setup.title', 'Set Up Two-Factor Authentication')}</h2>
          <button className="mfa-close-btn" onClick={onCancel}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="mfa-setup-progress">
          <div className={`mfa-progress-step ${step === 'password' ? 'active' : 'completed'}`}>1</div>
          <div className="mfa-progress-line"></div>
          <div className={`mfa-progress-step ${step === 'qrcode' ? 'active' : ['verify', 'recovery', 'complete'].includes(step) ? 'completed' : ''}`}>2</div>
          <div className="mfa-progress-line"></div>
          <div className={`mfa-progress-step ${step === 'verify' ? 'active' : ['recovery', 'complete'].includes(step) ? 'completed' : ''}`}>3</div>
          <div className="mfa-progress-line"></div>
          <div className={`mfa-progress-step ${step === 'recovery' ? 'active' : step === 'complete' ? 'completed' : ''}`}>4</div>
        </div>

        <div className="mfa-setup-content">
          {step === 'password' && renderPasswordStep()}
          {step === 'qrcode' && renderQRCodeStep()}
          {step === 'verify' && renderVerifyStep()}
          {step === 'recovery' && renderRecoveryStep()}
          {step === 'complete' && renderCompleteStep()}
        </div>
      </div>
    </div>
  );
};

export default MFASetup;
